/**
 * Etapa 1 — Faca seu cliente cadastrar online direto pelo app.
 *
 * Layout estruturado conforme mockup aprovado:
 *  - Header com titulo + subtitulo
 *  - 2 cards superiores: "Modelo pronto" e "Crie sua propria linha"
 *  - 6 cards fixos numerados:
 *      1. Dados pessoais (CPF+Nome+DN via Receita Federal, CEP via ViaCEP, Endereco)
 *      2. Contatos (WhatsApp, E-mail, Telefone — todos validados via API)
 *      3. Referencias pessoais (quantidade 1-5)
 *      4. Documentos (lista editavel: RG Frente, RG Verso, Selfie, ...)
 *      5. Fotos e videos adicionais (nome, exemplo, quantidade, ordem, tipo)
 *      6. Chave Pix (API Validacao)
 *  - Bloco final "Avisos que voce pode ativar"
 *
 * NAO ha FlowEditor. Toda configuracao e estruturada e tipada.
 */

import { useTranslation } from "react-i18next";
import type { Dispatch, SetStateAction } from "react";
import type { PlanFormState } from "../types";
import type {
  DocumentoObrigatorioItem,
  FotoVideoItem,
  OnboardingConfig,
} from "../../../../services/charge-plans.service";
import FlowEditor, { type OnboardingStep } from "../../../../components/FlowEditor";

interface Props {
  state: PlanFormState;
  setState: Dispatch<SetStateAction<PlanFormState>>;
}

/** Documentos pre-definidos que o gestor pode habilitar via toggle no Card 4. */
const DOCUMENTOS_DISPONIVEIS: { id: string; labelKey: string }[] = [
  { id: "rg", labelKey: "charge_plans.step1_onboarding.card_docs.docs.rg" },
  { id: "cpf", labelKey: "charge_plans.step1_onboarding.card_docs.docs.cpf" },
  { id: "cnh", labelKey: "charge_plans.step1_onboarding.card_docs.docs.cnh" },
  {
    id: "carteira_trabalho",
    labelKey: "charge_plans.step1_onboarding.card_docs.docs.carteira_trabalho",
  },
  {
    id: "titulo_eleitor",
    labelKey: "charge_plans.step1_onboarding.card_docs.docs.titulo_eleitor",
  },
  { id: "passaporte", labelKey: "charge_plans.step1_onboarding.card_docs.docs.passaporte" },
];

export function Step1Onboarding({ state, setState }: Props) {
  const { t } = useTranslation();
  const cfg = state.onboardingConfig;

  const updateConfig = (patch: Partial<OnboardingConfig>) =>
    setState({
      ...state,
      onboardingConfig: { ...cfg, ...patch },
    });

  const updateSection = <K extends keyof OnboardingConfig>(
    key: K,
    patch: Partial<OnboardingConfig[K]>,
  ) =>
    setState({
      ...state,
      onboardingConfig: {
        ...cfg,
        [key]: { ...(cfg[key] as object), ...patch },
      },
    });

  return (
    <div className="flex flex-col gap-6">
      <header>
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-5 w-5"
            >
              <path d="M12 12c2.7 0 4.875-2.175 4.875-4.875S14.7 2.25 12 2.25 7.125 4.425 7.125 7.125 9.3 12 12 12Zm0 2.625c-3.252 0-9.75 1.628-9.75 4.875V21h19.5v-1.5c0-3.247-6.498-4.875-9.75-4.875Z" />
            </svg>
          </span>
          <h1 className="text-2xl font-bold text-foreground">
            {t("charge_plans.step1_onboarding.title")}
          </h1>
        </div>
      </header>

      {/* Top cards: Modelo pronto / Crie sua propria linha */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TopCard
          highlight={cfg.modeloPronto}
          onClick={() => updateConfig({ modeloPronto: true })}
          left={
            <div className="flex flex-col gap-1">
              <p className="text-sm text-foreground">
                <span className="font-semibold">
                  {t("charge_plans.step1_onboarding.modelo_pronto.line1_bold")}
                </span>{" "}
                {t("charge_plans.step1_onboarding.modelo_pronto.line1_rest")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("charge_plans.step1_onboarding.modelo_pronto.line2")}
              </p>
            </div>
          }
          right={
            <div className="flex flex-col items-end gap-2 text-right">
              <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-700">
                {t("charge_plans.step1_onboarding.modelo_pronto.badge")}
              </span>
              <p className="text-sm font-semibold text-foreground">
                {t("charge_plans.step1_onboarding.modelo_pronto.title")}
              </p>
              <p className="max-w-[220px] text-xs text-muted-foreground">
                {t("charge_plans.step1_onboarding.modelo_pronto.subtitle")}
              </p>
            </div>
          }
        />
        <TopCard
          highlight={!cfg.modeloPronto}
          onClick={() => updateConfig({ modeloPronto: false })}
          accent="emerald"
          left={
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                  {t("charge_plans.step1_onboarding.custom.badge")}
                </span>
                <p className="text-sm font-semibold text-emerald-700">
                  {t("charge_plans.step1_onboarding.custom.title")}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("charge_plans.step1_onboarding.custom.line1")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("charge_plans.step1_onboarding.custom.line2")}
              </p>
            </div>
          }
          right={
            <div className="flex flex-col items-end gap-1.5 text-xs text-muted-foreground">
              <CheckRow>
                {t("charge_plans.step1_onboarding.custom.bullet_info")}
              </CheckRow>
              <CheckRow>
                {t("charge_plans.step1_onboarding.custom.bullet_docs")}
              </CheckRow>
              <CheckRow>
                {t("charge_plans.step1_onboarding.custom.bullet_validations")}
              </CheckRow>
              <CheckRow>
                {t("charge_plans.step1_onboarding.custom.bullet_experience")}
              </CheckRow>
              <span
                aria-hidden
                className="mt-2 inline-flex items-center rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white"
              >
                {t("charge_plans.step1_onboarding.custom.cta")}
              </span>
            </div>
          }
        />
      </div>

      {/* Seta + cards numerados aparecem apenas no Modelo pronto (nosso fluxo) */}
      {cfg.modeloPronto && (
      <>
      <div className="flex flex-col items-center gap-1.5 py-1">
        <span
          aria-hidden
          className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700 shadow-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="h-5 w-5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m0 0 6-6m-6 6-6-6" />
          </svg>
        </span>
        <span className="text-xs font-medium text-muted-foreground">
          {t("charge_plans.step1_onboarding.arrow_hint")}
        </span>
      </div>

      {/* Cards 1-3 row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Card 1 — Dados pessoais */}
        <NumberedCard
          number={1}
          title={t("charge_plans.step1_onboarding.card_pessoais.title")}
          subtitle={t("charge_plans.step1_onboarding.card_pessoais.subtitle")}
          editable
        >
          <ToggleRow
            checked={cfg.dadosPessoais.cpfNomeDn}
            onChange={(v) => updateSection("dadosPessoais", { cpfNomeDn: v, habilitado: v || cfg.dadosPessoais.cep || cfg.dadosPessoais.enderecoCompleto })}
            label={t("charge_plans.step1_onboarding.card_pessoais.cpf_label")}
            badge={t("charge_plans.step1_onboarding.card_pessoais.cpf_badge")}
            badgeTone="primary"
            hint={t("charge_plans.step1_onboarding.card_pessoais.cpf_hint")}
          />
          <Divider label={t("charge_plans.step1_onboarding.card_pessoais.address_label")} />
          <ToggleRow
            checked={cfg.dadosPessoais.cep}
            onChange={(v) => updateSection("dadosPessoais", { cep: v, habilitado: cfg.dadosPessoais.cpfNomeDn || v || cfg.dadosPessoais.enderecoCompleto })}
            label={t("charge_plans.step1_onboarding.card_pessoais.cep_label")}
            badge={t("charge_plans.step1_onboarding.card_pessoais.cep_badge")}
            badgeTone="primary"
            hint={t("charge_plans.step1_onboarding.card_pessoais.cep_hint")}
          />
          <ToggleRow
            checked={cfg.dadosPessoais.enderecoCompleto}
            onChange={(v) =>
              updateSection("dadosPessoais", { enderecoCompleto: v, habilitado: cfg.dadosPessoais.cpfNomeDn || cfg.dadosPessoais.cep || v })
            }
            label={t("charge_plans.step1_onboarding.card_pessoais.endereco_label")}
            hint={t("charge_plans.step1_onboarding.card_pessoais.endereco_hint")}
          />
        </NumberedCard>

        {/* Card 2 — Contatos */}
        <NumberedCard
          number={2}
          title={t("charge_plans.step1_onboarding.card_contatos.title")}
          subtitle={t("charge_plans.step1_onboarding.card_contatos.subtitle")}
          editable
        >
          <ToggleRow
            checked={cfg.contatos.whatsapp}
            onChange={(v) =>
              updateSection("contatos", {
                whatsapp: v,
                habilitado: v || cfg.contatos.email,
              })
            }
            label={t("charge_plans.step1_onboarding.card_contatos.wa_label")}
            badge={t("charge_plans.step1_onboarding.card_contatos.wa_badge")}
            badgeTone="success"
            hint={t("charge_plans.step1_onboarding.card_contatos.wa_hint")}
          />
          <ToggleRow
            checked={cfg.contatos.email}
            onChange={(v) =>
              updateSection("contatos", {
                email: v,
                habilitado: cfg.contatos.whatsapp || v,
              })
            }
            label={t("charge_plans.step1_onboarding.card_contatos.email_label")}
            badge={t("charge_plans.step1_onboarding.card_contatos.email_badge")}
            badgeTone="success"
            hint={t("charge_plans.step1_onboarding.card_contatos.email_hint")}
          />
        </NumberedCard>

        {/* Card 3 — Referencias pessoais */}
        <NumberedCard
          number={3}
          title={t("charge_plans.step1_onboarding.card_refs.title")}
          subtitle={t("charge_plans.step1_onboarding.card_refs.subtitle")}
          editable
        >
          <div className="flex items-center justify-between gap-3">
            <label className="text-sm font-medium text-foreground">
              {t("charge_plans.step1_onboarding.card_refs.qty_label")}
            </label>
            <select
              value={cfg.referencias.quantidade}
              onChange={(e) =>
                updateSection("referencias", {
                  quantidade: parseInt(e.target.value, 10),
                  habilitado: true,
                })
              }
              className="h-9 rounded-lg border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}{" "}
                  {n === 1
                    ? t("charge_plans.step1_onboarding.card_refs.qty_singular")
                    : t("charge_plans.step1_onboarding.card_refs.qty_plural")}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("charge_plans.step1_onboarding.card_refs.subhint")}
          </p>
          <CheckRow>
            {t("charge_plans.step1_onboarding.card_refs.bullet_phone")}
          </CheckRow>
          <CheckRow>
            {t("charge_plans.step1_onboarding.card_refs.bullet_relation")}
          </CheckRow>
        </NumberedCard>
      </div>

      {/* Cards 4-6 row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Card 4 — Documentos */}
        <NumberedCard
          number={4}
          title={t("charge_plans.step1_onboarding.card_docs.title")}
          subtitle={t("charge_plans.step1_onboarding.card_docs.subtitle")}
          editable
        >
          {DOCUMENTOS_DISPONIVEIS.map((d) => {
            const ativo = cfg.documentos.lista.some((x) => x.id === d.id);
            return (
              <ToggleRow
                key={d.id}
                checked={ativo}
                onChange={(v) => {
                  const next = v
                    ? [
                        ...cfg.documentos.lista,
                        { id: d.id, nome: t(d.labelKey), label: "" } as DocumentoObrigatorioItem,
                      ]
                    : cfg.documentos.lista.filter((x) => x.id !== d.id);
                  updateSection("documentos", {
                    lista: next,
                    habilitado: next.length > 0,
                  });
                }}
                label={t(d.labelKey)}
              />
            );
          })}
        </NumberedCard>

        {/* Card 5 — Fotos e videos adicionais */}
        <NumberedCard
          number={5}
          title={t("charge_plans.step1_onboarding.card_media.title")}
          subtitle={t("charge_plans.step1_onboarding.card_media.subtitle")}
          editable
        >
          <ul className="flex flex-col gap-2">
            {cfg.fotosVideos.lista.map((item, idx) => (
              <li
                key={item.id}
                className="flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-2"
              >
                <div className="flex items-center justify-between gap-2">
                  {item.obrigatorio ? (
                    <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-700">
                      {t("charge_plans.step1_onboarding.card_docs.required_badge")}
                    </span>
                  ) : (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                      {t("charge_plans.step1_onboarding.card_docs.added_badge")}
                    </span>
                  )}
                  {item.obrigatorio ? null : (
                    <button
                      type="button"
                      onClick={() => {
                        const next = cfg.fotosVideos.lista.filter((_, i) => i !== idx);
                        updateSection("fotosVideos", { lista: next, habilitado: next.length > 0 });
                      }}
                      aria-label={t("common.delete")}
                      title={t("common.delete")}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-rose-600 hover:bg-rose-50"
                    >
                      🗑️
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    {t("charge_plans.step1_onboarding.card_media.col_name")}
                  </label>
                  <input
                    type="text"
                    value={item.nome}
                    placeholder={t("charge_plans.step1_onboarding.card_media.name_placeholder")}
                    onChange={(e) => {
                      const next = [...cfg.fotosVideos.lista];
                      next[idx] = { ...item, nome: e.target.value };
                      updateSection("fotosVideos", { lista: next, habilitado: true });
                    }}
                    className="h-8 w-full min-w-0 rounded-md border border-input bg-card px-2 text-xs"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    {t("charge_plans.step1_onboarding.card_media.col_example")}
                  </label>
                  <input
                    type="text"
                    value={item.exemplo}
                    placeholder={t("charge_plans.step1_onboarding.card_media.example_placeholder")}
                    onChange={(e) => {
                      const next = [...cfg.fotosVideos.lista];
                      next[idx] = { ...item, exemplo: e.target.value };
                      updateSection("fotosVideos", { lista: next, habilitado: true });
                    }}
                    className="h-8 w-full min-w-0 rounded-md border border-input bg-card px-2 text-xs"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t("charge_plans.step1_onboarding.card_media.col_qty")}
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={item.quantidade}
                      onChange={(e) => {
                        const v = Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1));
                        const next = [...cfg.fotosVideos.lista];
                        next[idx] = { ...item, quantidade: v };
                        updateSection("fotosVideos", { lista: next, habilitado: true });
                      }}
                      className="h-8 w-full min-w-0 rounded-md border border-input bg-card px-2 text-xs text-center"
                    />
                  </div>
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t("charge_plans.step1_onboarding.card_media.col_order")}
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={item.ordem}
                      onChange={(e) => {
                        const v = Math.max(1, parseInt(e.target.value, 10) || 1);
                        const next = [...cfg.fotosVideos.lista];
                        next[idx] = { ...item, ordem: v };
                        updateSection("fotosVideos", { lista: next, habilitado: true });
                      }}
                      className="h-8 w-full min-w-0 rounded-md border border-input bg-card px-2 text-xs text-center"
                    />
                  </div>
                  <div className="flex flex-col gap-1 min-w-0">
                    <label className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t("charge_plans.step1_onboarding.card_media.col_type")}
                    </label>
                    <select
                      value={item.tipo}
                      onChange={(e) => {
                        const next = [...cfg.fotosVideos.lista];
                        next[idx] = { ...item, tipo: e.target.value as FotoVideoItem["tipo"] };
                        updateSection("fotosVideos", { lista: next, habilitado: true });
                      }}
                      className="h-8 w-full min-w-0 rounded-md border border-input bg-card px-1 text-xs"
                    >
                      <option value="foto">
                        {t("charge_plans.step1_onboarding.card_media.type_foto")}
                      </option>
                      <option value="video">
                        {t("charge_plans.step1_onboarding.card_media.type_video")}
                      </option>
                      <option value="selfie">
                        {t("charge_plans.step1_onboarding.card_media.type_selfie")}
                      </option>
                    </select>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => {
              const ordens = cfg.fotosVideos.lista.map((i) => i.ordem);
              const nextOrdem = ordens.length ? Math.max(...ordens) + 1 : 1;
              const novo: FotoVideoItem = {
                id: `mid_${Date.now()}`,
                nome: "",
                exemplo: "",
                quantidade: 1,
                ordem: nextOrdem,
                tipo: "foto",
                obrigatorio: false,
              };
              updateSection("fotosVideos", {
                lista: [...cfg.fotosVideos.lista, novo],
                habilitado: true,
              });
            }}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-input bg-card py-2.5 text-sm font-medium text-muted-foreground hover:border-primary-300 hover:text-primary-700"
          >
            +{" "}
            {t("charge_plans.step1_onboarding.card_media.add_button")}
          </button>

          <Callout tone="info">
            {t("charge_plans.step1_onboarding.card_media.callout")}
          </Callout>
        </NumberedCard>

        {/* Card 6 — Chave Pix */}
        <NumberedCard
          number={6}
          title={t("charge_plans.step1_onboarding.card_pix.title")}
          subtitle={t("charge_plans.step1_onboarding.card_pix.subtitle")}
          editable
        >
          <ToggleRow
            checked={cfg.chavePix.habilitado}
            onChange={(v) => updateSection("chavePix", { habilitado: v })}
            label={t("charge_plans.step1_onboarding.card_pix.toggle_label")}
            badge={t("charge_plans.step1_onboarding.card_pix.badge")}
            badgeTone="success"
            hint={t("charge_plans.step1_onboarding.card_pix.hint")}
          />
        </NumberedCard>
      </div>
      </>
      )}

      {/* Fluxo customizado: FlowEditor aparece quando o gestor escolhe
          "Crie sua propria linha de cadastro" (modeloPronto = false). */}
      {!cfg.modeloPronto && (
        <section className="flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-card p-5">
          <header className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
                ✎
              </span>
              <h3 className="text-sm font-bold text-foreground">
                {t("charge_plans.step1_onboarding.custom_flow.title")}
              </h3>
            </div>
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
              {t("charge_plans.step1_onboarding.custom_flow.badge")}
            </span>
          </header>
          <p className="text-xs text-muted-foreground">
            {t("charge_plans.step1_onboarding.custom_flow.subtitle")}
          </p>
          <div className="h-[600px] overflow-hidden rounded-xl border border-border">
            <FlowEditor
              steps={(cfg.customFlowSteps as OnboardingStep[] | undefined) ?? []}
              onChange={(steps) =>
                setState({
                  ...state,
                  onboardingConfig: { ...cfg, customFlowSteps: steps },
                })
              }
              /*
               * Passa as parcelas configuradas no Step3 — preview do simulador
               * usa o 1o plano ativo pra renderizar valor da parcela / taxa
               * em vez de placeholder. Sem planos cai no R$ 0,00.
               */
              simulatorPlans={state.installments
                .filter((i) => i.ativo !== false)
                .map((i) => ({
                  tipo: i.tipo,
                  parcelas: i.numParcelas,
                  taxaPct: i.taxaPct,
                  label: i.label ?? null,
                }))}
              simulatorValorMinimo={state.valorMinimo}
            />
          </div>
        </section>
      )}

    </div>
  );
}

// ============ Sub-components ============

function TopCard({
  highlight,
  onClick,
  left,
  right,
  accent = "primary",
}: {
  highlight: boolean;
  onClick: () => void;
  left: React.ReactNode;
  right: React.ReactNode;
  accent?: "primary" | "emerald";
}) {
  const borderClass = highlight
    ? accent === "emerald"
      ? "border-emerald-500 bg-emerald-50/40 shadow-sm"
      : "border-primary-400 bg-primary-50/40 shadow-sm"
    : "border-border bg-card hover:border-primary-200";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-stretch justify-between gap-6 rounded-2xl border p-5 text-left transition ${borderClass}`}
    >
      <div className="flex flex-1 items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            accent === "emerald"
              ? "bg-emerald-100 text-emerald-700"
              : "bg-primary-100 text-primary-700"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            className="h-5 w-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25"
            />
          </svg>
        </span>
        {left}
      </div>
      <div className="flex-1">{right}</div>
    </button>
  );
}

function NumberedCard({
  number,
  title,
  subtitle,
  editable,
  badge,
  children,
}: {
  number: number;
  title: string;
  subtitle: string;
  editable?: boolean;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
            {number}
          </span>
          <h3 className="text-sm font-bold text-foreground">{title}</h3>
          {badge ? (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-700">
              {badge}
            </span>
          ) : null}
        </div>
        {editable ? (
          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-700">
            ✎ {/* editavel */}
          </span>
        ) : null}
      </header>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
  badge,
  badgeTone = "primary",
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  badge?: string;
  badgeTone?: "primary" | "success";
  hint?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
          checked ? "bg-primary-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-words text-sm font-medium text-foreground">
            {label}
          </span>
          {badge ? (
            <span
              className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                badgeTone === "success"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-primary-100 text-primary-700"
              }`}
            >
              {badge}
            </span>
          ) : null}
        </div>
        {hint ? (
          <p className="break-words text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </div>
    </div>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

function Callout({
  tone = "info",
  children,
}: {
  tone?: "info";
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${
        tone === "info"
          ? "border-primary-100 bg-primary-50 text-primary-900"
          : "border-border bg-muted text-foreground"
      }`}
    >
      <span aria-hidden>ℹ️</span>
      <span>{children}</span>
    </div>
  );
}

function CheckRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-xs text-muted-foreground">
      <span className="mt-0.5 text-emerald-600">✓</span>
      <span>{children}</span>
    </div>
  );
}

